import { createPortal } from "react-dom";
import { Loader2, Download, CheckCircle2, Palette, Clock } from "lucide-react";

// Centered modal shown while an export is prepared and downloaded.
// phase: "preparing" → indeterminate animated bar; "downloading" → real 0→100% bar.
// Rendered through a portal to document.body so its position:fixed always anchors to the
// viewport — a transformed ancestor (e.g. the animate-pop viewer) would otherwise capture it.
export const DownloadProgressModal = ({ open, phase, percent, filename, fmt, previousAt, note, onCancel, onRepaint, onDownloadAnyway }) => {
  if (!open) return null;
  if (phase === "expired" || phase === "repainting") {
    const repainting = phase === "repainting";
    const made = previousAt ? new Date(previousAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null;
    return createPortal(
      <div data-testid="download-expired-modal" className="fixed inset-0 z-[100] grid place-items-center bg-background/70 backdrop-blur-sm animate-pop" role="alertdialog" aria-busy={repainting}>
        <div className="mx-4 w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-2xl duck-shadow">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-accent/15 text-accent">
              {repainting ? <Loader2 className="h-6 w-6 animate-spin" /> : <Clock className="h-6 w-6" />}
            </span>
            <p className="font-heading text-lg font-bold leading-tight">{repainting ? "Repainting your duck…" : "Your previous 3MF has expired"}</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground" data-testid="download-expired-text">
            {repainting
              ? "Refreshing the colours and preview images. This takes about half a minute, then your download starts."
              : `The 3MF for this duck${made ? ` (made ${made})` : ""} was cleared after 24 hours. Repaint & preview first so the file uses the latest paint and matches the preview, then download.`}
          </p>
          {!repainting && (
            <div className="mt-5 space-y-2">
              <button type="button" data-testid="download-expired-repaint-btn" onClick={onRepaint} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-bold text-primary-foreground">
                <Palette className="h-4 w-4" /> Repaint & preview, then download
              </button>
              <button type="button" data-testid="download-expired-anyway-btn" onClick={onDownloadAnyway} className="w-full rounded-full border border-border py-2.5 text-sm font-semibold transition-colors hover:bg-muted">
                Download without repainting
              </button>
              <button type="button" data-testid="download-expired-cancel-btn" onClick={onCancel} className="w-full rounded-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body,
    );
  }

  const preparing = phase === "preparing";
  const done = !preparing && percent >= 100;

  return createPortal(
    <div
      data-testid="download-progress-modal"
      className="fixed inset-0 z-[100] grid place-items-center bg-background/70 backdrop-blur-sm animate-pop"
      aria-live="polite"
      role="alertdialog"
      aria-busy={!done}
    >
      <div className="mx-4 w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-2xl duck-shadow">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-accent/15 text-accent">
            {done ? (
              <CheckCircle2 className="h-6 w-6" data-testid="download-done-icon" />
            ) : preparing ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Download className="h-6 w-6" />
            )}
          </span>
          <div className="min-w-0">
            <p className="font-heading text-lg font-bold leading-tight" data-testid="download-progress-title">
              {done ? "Download ready" : preparing ? "Preparing your file…" : "Downloading…"}
            </p>
            <p className="truncate text-sm text-muted-foreground" data-testid="download-progress-filename" title={filename}>
              {filename || `${(fmt || "").toUpperCase()} export`}
            </p>
          </div>
        </div>

        <div className="mt-5">
          <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-primary/15">
            {preparing ? (
              <span className="absolute inset-y-0 animate-indeterminate rounded-full bg-accent" />
            ) : (
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-accent transition-[width] duration-200 ease-out"
                style={{ width: `${percent}%` }}
                data-testid="download-progress-bar"
              />
            )}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-mono2 text-muted-foreground">
            <span>{preparing ? "Building 3D mesh on the server" : done ? "Saved to your device" : "Transferring"}</span>
            <span data-testid="download-progress-percent">{preparing ? "" : `${percent}%`}</span>
          </div>
        </div>

        {note && (
          <p data-testid="download-note" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {note}
          </p>
        )}

        {!done && onCancel && (
          <button
            type="button"
            data-testid="download-cancel-btn"
            onClick={onCancel}
            className="mt-5 w-full rounded-full border border-border py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Cancel
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};
